import { defineStore } from "pinia";

/**
 * Auth store factory, one instance per app/plane.
 *
 * Holds NO credential. The session is an httpOnly cookie the browser manages and
 * JavaScript cannot read, so this store only tracks who the server says we are.
 *
 * That changes one thing meaningfully: session restore is no longer "is there a
 * token in storage?" but "does the server still accept my cookie?". It is a
 * network call, and it is the honest question — a token in storage was never
 * proof of a valid session anyway, just proof that one existed once.
 */
export function createAuthStore({ id, api, loginPath, mfaPath, mePath, logoutPath }) {
  return defineStore(id, {
    state: () => ({
      user: null,
      loading: false,
      error: null,
      initialized: false,

      /**
       * Held in memory only, never persisted. It proves the password step passed
       * and is exchanged for a session by the MFA step. Writing it to storage
       * would leave a half-authenticated credential on disk.
       */
      challengeToken: null,
    }),

    getters: {
      isAuthenticated: (state) => !!state.user,
      mfaPending: (state) => !!state.challengeToken,
    },

    actions: {
      /**
       * Password step. Returns "ok" when a session was issued, "mfa" when a
       * second factor is required, or false on failure.
       *
       * On success the server sets the cookie; there is nothing to store here.
       */
      async login(username, password, kind = null, phonePrefix = null) {
        this.loading = true;
        this.error = null;

        try {
          // `kind` names which identifier was typed — username, email or phone.
          // Sent rather than inferred: guessing from the shape of the string
          // lets one input address two columns, which is how a player ends up
          // signed into somebody else's account.
          const { data } = await api.client.post(loginPath, { username, password, kind, phonePrefix });

          if (data.mfaRequired) {
            this.challengeToken = data.challengeToken;
            return "mfa";
          }

          this.user = { ...data };
          return "ok";
        } catch (err) {
          this.error = err.message;
          this.user = null;
          this.challengeToken = null;
          return false;
        } finally {
          this.loading = false;
        }
      },

      /** Second factor. Exchanges the challenge token for a session cookie. */
      async verifyMfa(code) {
        if (!this.challengeToken) {
          this.error = "Your sign-in attempt expired. Please start again.";
          return false;
        }

        this.loading = true;
        this.error = null;

        try {
          const { data } = await api.client.post(mfaPath, {
            challengeToken: this.challengeToken,
            code,
          });

          this.user = { ...data };
          this.challengeToken = null;
          return true;
        } catch (err) {
          this.error = err.message;
          return false;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Restores a session on page reload by asking the server.
       *
       * A 401 here is the normal "not signed in" case, not an error worth
       * showing — the browser simply had no cookie, or it had expired.
       */
      async restore() {
        try {
          // expectUnauthorized: a 401 here means "nobody is signed in", which is
          // the answer this call exists to get. Without the flag the shared
          // interceptor reads it as an expired session and redirects.
          const { data } = await api.client.get(mePath, { expectUnauthorized: true });
          this.user = { ...data };
          return true;
        } catch {
          this.user = null;
          return false;
        } finally {
          this.initialized = true;
        }
      },

      /**
       * Signs out server-side, which revokes the session row and clears the
       * cookie. A real logout — the credential is dead everywhere, not merely
       * forgotten by this tab.
       */
      async logout() {
        try {
          await api.client.post(logoutPath);
        } catch {
          // Even if the call fails, drop local state: the user asked to leave.
        } finally {
          this.user = null;
          this.challengeToken = null;
          this.error = null;
        }
      },

      cancelMfa() {
        this.challengeToken = null;
        this.error = null;
      },
    },
  });
}
